import type { Membership } from '../../../lib/api';

/**
 * Per-tier flat-amount editor — one row per tier with a number input. Used by
 * sign_up_bonus and sign_up_vortex which store a top-level `<configKey>: number`
 * on each membership's config jsonb. An OPTIONAL second column
 * (`secondaryConfigKey`) edits a parallel amount on the same row — used by
 * sign_up_bonus to surface its GC sibling (`sign_up_bonus_gc`) beside the cash
 * amount. Each cell deletes its key when set to 0.
 */
export function SignUpAmountPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
  configKey,
  hint,
  amountLabel = 'Amount',
  secondaryConfigKey,
  secondaryLabel,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
  configKey: string;
  hint?: string;
  amountLabel?: string;
  secondaryConfigKey?: string;
  secondaryLabel?: string;
}) {
  const numValue = (current: Record<string, unknown>, key: string) => {
    const raw = current[key];
    return typeof raw === 'number' || typeof raw === 'string' ? Number(raw) : 0;
  };
  const setKey = (current: Record<string, unknown>, key: string, n: number) => {
    const next = { ...current };
    if (n === 0) delete next[key];
    else next[key] = n;
    return next;
  };
  return (
    <div className="space-y-2">
      {hint ? <p className="text-[10px] text-muted-foreground">{hint}</p> : null}
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Tier</th>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">{amountLabel}</th>
              {secondaryConfigKey ? (
                <th className="px-3 py-2 font-medium">{secondaryLabel ?? 'GC'}</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => {
              const current = draft[m.id] ?? {};
              return (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono">{m.code}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{m.name}</td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-32 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={numValue(current, configKey)}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      onChange={(e) =>
                        onChange(m.id, setKey(current, configKey, Number(e.target.value) || 0))
                      }
                    />
                  </td>
                  {secondaryConfigKey ? (
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-32 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={numValue(current, secondaryConfigKey)}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          onChange(
                            m.id,
                            setKey(current, secondaryConfigKey, Number(e.target.value) || 0),
                          )
                        }
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
